import { AxiosError, AxiosResponse } from 'axios';
import { action, actionBound, observable, runInAction } from 'mobx';
import { Notice, UiResponse } from '../CommonInterfaces';
import { API } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import UiStore from './UiStore';
import { ACCOUNT, View } from '../Routes';
import MergeStore from './MergeStore';
import { waitForModalAnimation } from '../util/AnimationUtil';
import EmailLoginStore from './EmailLoginStore';

export interface AddEmailResponse extends UiResponse {
  duplicateProfileMessage: string;
  emailError: string;
  success: boolean;
  error?: boolean;
}

export default class AddEmailStore {
  @observable accessor fetching: boolean = false;
  @observable accessor email: string = '';
  @observable accessor response: AddEmailResponse | null = null;
  @observable accessor sessionValid: boolean = false;
  @observable accessor notice: Notice | null = null;
  @observable accessor addEmailModalOpen: boolean = false;

  private routeStore: RouteStore;
  private uiStore: UiStore;
  private mergeStore: MergeStore;
  private emailLoginStore: EmailLoginStore;

  constructor(rootStore: RootStore) {
    this.uiStore = rootStore.uiStore;
    this.routeStore = rootStore.routeStore;
    this.mergeStore = rootStore.mergeStore;
    this.emailLoginStore = rootStore.emailLoginStore;
  }

  @actionBound
  public setEmail(email: string) {
    this.email = email;
  }

  @actionBound
  public setResponse(response: AddEmailResponse | null) {
    this.response = response;
  }

  @actionBound
  public checkSessionValid = () => {
    this.fetching = true;
    return API.get(`/email/checkSessionValid${this.routeStore.formForwardParams}`).then(this.handleSessionValidSuccess).catch(this.handleSessionError);
  };

  public handleSubmit = (recaptchaToken: string | null | undefined) => {
    runInAction(() => {
      this.fetching = true;
    });
    return API.post(`/email/addEmail${this.routeStore.formForwardParams}`, { email: this.email, captchaToken: recaptchaToken })
      .then(this.handleSubmitSuccess)
      .catch((e: AxiosError) => this.handleSessionError(e));
  };

  @actionBound
  public setAddEmailModalState(isOpen: boolean) {
    this.addEmailModalOpen = isOpen;
  }

  @actionBound
  private handleSessionError(e: AxiosError) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    this.fetching = false;
    this.addEmailModalOpen = false;
    this.uiStore.showErrorNoticeFromResponse(e?.response);
    this.notice = { type: 'error', children: 'error.general' };
  }

  @actionBound
  private handleSessionValidSuccess(response: AxiosResponse<UiResponse>) {
    this.sessionValid = !response.data?.notice;
    this.fetching = false;
  }

  @actionBound
  private handleSubmitSuccess(response: AxiosResponse<AddEmailResponse>) {
    this.response = response.data;
    this.setAddEmailModalState(false);
    this.fetching = false;
    this.uiStore.hideNotices();
    if (this.response.error) {
      this.uiStore.showErrorNoticeFromResponse(response);
      return;
    }
    if (this.response?.component === View.ACCOUNT_MERGE) {
      this.routeStore.route(ACCOUNT + this.routeStore.formForwardParamsAnd({ service: 'addEmail', ForceAuth: 'true', goto: ACCOUNT }));
      this.emailLoginStore.emailLoginCredentials.emailAddress = this.email;
      waitForModalAnimation(() => this.mergeStore.setMergeLoginModalState(true));
      return;
    }
    if (this.response.duplicateProfileMessage) {
      this.uiStore.showErrorNotice(this.response.duplicateProfileMessage);
      return;
    }
    this.uiStore.showSuccessNotice('account.email.add.started', { email: this.email });
  }
}
